/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Stack,
  Skeleton,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as RejectIcon,
  Merge as MergeIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Help as UnknownIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useEntityContext } from './EntityContextSwitch';
import api from '../services/api';

// Entity type icon mapping
const EntityTypeIcon = ({ type }) => {
  switch (type) {
    case 'PERSON':
      return <PersonIcon fontSize="small" />;
    case 'ORGANIZATION':
      return <BusinessIcon fontSize="small" />;
    case 'LOCATION':
      return <LocationIcon fontSize="small" />;
    default:
      return <UnknownIcon fontSize="small" />;
  }
};

// Confidence level colors
const getConfidenceColor = (confidence) => {
  switch (confidence) {
    case 'HIGH':
      return 'success';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'error';
    default:
      return 'default';
  }
};

// eslint-disable-next-line max-lines-per-function
function EntityDiscoveryManagement() {
  const { user, isPlatformAdmin } = useAuth();
  const { currentEntity } = useEntityContext();
  const [discoveries, setDiscoveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [matchDialog, setMatchDialog] = useState({ open: false, discovery: null, matches: null });
  const [createDialog, setCreateDialog] = useState({ open: false, discovery: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, discovery: null });
  const [createForm, setCreateForm] = useState({ entityName: '', entityType: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [filters, setFilters] = useState({
    entityType: '',
    minConfidence: '',
  });

  useEffect(() => {
    if (currentEntity?.id) {
      fetchDiscoveries();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntity, filters]);

  const fetchDiscoveries = async () => {
    if (!currentEntity?.id) return;

    setLoading(true);
    setError(null);

    try {
      const params = {
        sourceEntityId: currentEntity.id,
      };
      if (filters.entityType) params.suggestedType = filters.entityType;
      if (filters.minConfidence) params.minConfidence = filters.minConfidence;

      const response = await api.getPendingEntityDiscoveries(params);
      setDiscoveries(response.discoveries || []);
    } catch (err) {
      setError('Failed to load discovered entities: ' + err.message);
      console.error('Error fetching discoveries:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async (discovery) => {
    setMatchDialog({ open: true, discovery, matches: null, loading: true });

    try {
      const response = await api.getEntityMatchSuggestions(discovery.id);
      setMatchDialog(prev => ({
        ...prev,
        matches: response,
        loading: false
      }));
    } catch (err) {
      setError('Failed to get match suggestions: ' + err.message);
      setMatchDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleConfirmMatch = async (targetEntityId, detokenizedName) => {
    try {
      await api.confirmEntityTokenAssociation({
        discoveredEntityId: matchDialog.discovery.id,
        targetEntityId,
        notes: `Matched to existing entity (detokenized: ${detokenizedName})`,
      });

      setSuccessMessage('Entity match confirmed');
      setMatchDialog({ open: false, discovery: null, matches: null });
      fetchDiscoveries();
    } catch (err) {
      setError('Failed to confirm match: ' + err.message);
    }
  };

  const handleCreateEntity = async () => {
    const { discovery } = createDialog;

    if (!createForm.entityName || !createForm.entityType) {
      setError('Please provide entity name and type');
      return;
    }

    try {
      await api.createEntityFromDiscovery(discovery.id, {
        entityName: createForm.entityName,
        entityType: createForm.entityType,
        notes: `Created from discovered token ${discovery.entityInfo.suggestedName}`
      });

      setSuccessMessage('New entity created');
      setCreateDialog({ open: false, discovery: null });
      setCreateForm({ entityName: '', entityType: '' });
      fetchDiscoveries();
    } catch (err) {
      setError('Failed to create entity: ' + err.message);
    }
  };

  const handleReject = async () => {
    const { discovery } = rejectDialog;

    if (!rejectReason) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      await api.rejectDiscoveredEntity(discovery.id, {
        reason: rejectReason
      });

      setSuccessMessage('Discovery rejected');
      setRejectDialog({ open: false, discovery: null });
      setRejectReason('');
      fetchDiscoveries();
    } catch (err) {
      setError('Failed to reject discovery: ' + err.message);
    }
  };

  // Check if user has entity management permissions
  // System-wide admin or entity-specific 'entity' role
  const [userRoles, setUserRoles] = useState(null);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      if (!currentEntity?.id || !user?.email) {
        setCheckingPermissions(false);
        return;
      }

      try {
        const rolesData = await api.getEntityUserRolesByEmail(user.email, currentEntity.id);
        setUserRoles(rolesData);
      } catch (err) {
        console.error('Error checking permissions:', err);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, [currentEntity, user]);

  const canManageEntities = isPlatformAdmin || userRoles?.entityRoles?.includes('entity');

  if (checkingPermissions) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canManageEntities) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You need admin or entity manager role to access this feature.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Entity Discovery Management</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDiscoveries}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <FormControl fullWidth size="small">
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={filters.entityType}
                onChange={(e) => setFilters(prev => ({ ...prev, entityType: e.target.value }))}
                label="Entity Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="PERSON">Person</MenuItem>
                <MenuItem value="ORGANIZATION">Organization</MenuItem>
                <MenuItem value="LOCATION">Location</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <FormControl fullWidth size="small">
              <InputLabel>Min Confidence</InputLabel>
              <Select
                value={filters.minConfidence}
                onChange={(e) => setFilters(prev => ({ ...prev, minConfidence: e.target.value }))}
                label="Min Confidence"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <Typography variant="body2" color="text.secondary">
              {discoveries.length} discovered entities pending review
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      {/* Discoveries Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Token</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Discovered</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : discoveries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" sx={{ py: 2 }}>
                    No pending entity discoveries
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              discoveries.map((discovery) => (
                <TableRow key={discovery.id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {discovery.entityInfo.suggestedName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<EntityTypeIcon type={discovery.entityInfo.suggestedType} />}
                      label={discovery.entityInfo.suggestedType}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={discovery.entityInfo.confidence}
                      size="small"
                      color={getConfidenceColor(discovery.entityInfo.confidence)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {discovery.discoveryContext.sourceDataPath}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {discovery.discoveredAt ?
                      new Date(discovery.discoveredAt._seconds ?
                        discovery.discoveredAt._seconds * 1000 :
                        discovery.discoveredAt).toLocaleDateString() :
                      'Unknown'
                    }
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Find matches">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => fetchMatches(discovery)}
                        >
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Create new entity">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setCreateForm({ entityName: '', entityType: discovery.entityInfo.suggestedType || '' });
                            setCreateDialog({ open: true, discovery });
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setRejectReason('');
                            setRejectDialog({ open: true, discovery });
                          }}
                        >
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Match Suggestions Dialog */}
      <Dialog
        open={matchDialog.open}
        onClose={() => setMatchDialog({ open: false, discovery: null, matches: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Entity Match Suggestions
          {matchDialog.matches && (
            <Typography variant="body2" color="text.secondary">
              Detokenized: {matchDialog.matches.detokenizedName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {matchDialog.loading ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Box>
          ) : matchDialog.matches?.potentialMatches?.length > 0 ? (
            <List>
              {matchDialog.matches.potentialMatches.map((match, index) => (
                <React.Fragment key={match.entityId}>
                  <ListItem>
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EntityTypeIcon type={match.entityType} />
                          <Typography>{match.entityName}</Typography>
                          <Chip
                            label={`${Math.round(match.similarityScore * 100)}%`}
                            size="small"
                            color={getConfidenceColor(match.confidence)}
                          />
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Box>
                          <Typography variant="caption">
                            Entity ID: {match.entityId}
                          </Typography>
                          {match.hasExistingTokens && (
                            <Chip
                              label="Has tokens"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<MergeIcon />}
                        onClick={() => handleConfirmMatch(match.entityId, matchDialog.matches.detokenizedName)}
                      >
                        Confirm Match
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < matchDialog.matches.potentialMatches.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No potential matches found. Consider creating a new entity.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDialog({ open: false, discovery: null, matches: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Create Entity Dialog */}
      <Dialog
        open={createDialog.open}
        onClose={() => setCreateDialog({ open: false, discovery: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Entity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Creating a new entity from token: {createDialog.discovery?.entityInfo.suggestedName}
            </Alert>
            <TextField
              label="Entity Name"
              fullWidth
              placeholder="Enter the actual entity name"
              autoFocus
              value={createForm.entityName}
              onChange={(e) => setCreateForm(prev => ({ ...prev, entityName: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel id="entity-type-select-label">Entity Type</InputLabel>
              <Select
                labelId="entity-type-select-label"
                label="Entity Type"
                value={createForm.entityType}
                onChange={(e) => setCreateForm(prev => ({ ...prev, entityType: e.target.value }))}
              >
                <MenuItem value="PERSON">Person</MenuItem>
                <MenuItem value="ORGANIZATION">Organization</MenuItem>
                <MenuItem value="LOCATION">Location</MenuItem>
                <MenuItem value="PROJECT">Project</MenuItem>
                <MenuItem value="PROGRAM">Program</MenuItem>
                <MenuItem value="EVENT">Event</MenuItem>
                <MenuItem value="RESOURCE">Resource</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog({ open: false, discovery: null })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateEntity}>
            Create Entity
          </Button>
        </DialogActions>
      </Dialog>
      {/* Reject Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, discovery: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Discovery</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Rejecting discovery for token: {rejectDialog.discovery?.entityInfo.suggestedName}
            </Alert>
            <TextField
              label="Reason for Rejection"
              fullWidth
              multiline
              rows={3}
              placeholder="e.g., Not an entity, duplicate, test data..."
              autoFocus
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, discovery: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EntityDiscoveryManagement;
